import { useCallback, useState } from 'react';
import { useSpacetimeDB } from 'spacetimedb/react';
import { type DbConnection as MinetwinConn } from '@sdk/minetwin';
import type { Toast } from '@/data/types';

/**
 * Hook returning a `runReducer` helper that centralizes the
 * connection check, the busy-state signal and the failure-toast for
 * MineTwin reducer calls. Extracted from Workforce.tsx (where it was
 * inlined) so other admin surfaces — Settings/ProjectAgentPicker,
 * future Orientation Assignment cleanups — can share one canonical
 * implementation.
 *
 * Caller pattern:
 *   const { runReducer, busy } = useRunReducer(toast);
 *   await runReducer('Set agent', (conn) => {
 *     conn.reducers.setProjectAgent({ projectId, agentIdentity, displayName });
 *     return true;
 *   });
 *
 * `busy` is the label of the currently-running reducer call, or null
 * when idle — feed it into your disabled props (`disabled={busy !==
 * null}`) so the UI is rendered consistently across surfaces. Success
 * toasts stay the caller's responsibility — only failures and the
 * connection-missing case are toasted here.
 */
export function useRunReducer(toast: (t: Omit<Toast, 'id'>) => void): {
  runReducer: <T>(label: string, fn: (conn: MinetwinConn) => T | Promise<T>) => Promise<T | null>;
  busy: string | null;
} {
  const minetwinState = useSpacetimeDB('minetwin');
  const [busy, setBusy] = useState<string | null>(null);

  const runReducer = useCallback(
    async <T,>(label: string, fn: (conn: MinetwinConn) => T | Promise<T>): Promise<T | null> => {
      const conn = minetwinState.getConnection() as MinetwinConn | null;
      if (!conn) {
        toast({ title: 'Not connected to MineTwin module', kind: 'bad' });
        return null;
      }
      setBusy(label);
      try {
        // Awaited so a callback that awaits its reducer call (which rejects
        // when the module refuses) lands in this catch, not in the global
        // unhandled-rejection toast after the caller already said "done".
        return await fn(conn);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        toast({ title: `${label} failed`, sub: message.slice(0, 200), kind: 'bad' });
        return null;
      } finally {
        setBusy(null);
      }
    },
    [minetwinState, toast],
  );

  return { runReducer, busy };
}
